import { Inject, Injectable, NgZone, OnDestroy, Optional } from '@angular/core';
import { environment } from '@environments/environment';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';
import { HttpService } from './http.service';

export const SESSION_IDLE_MS = environment.sessionIdleMinutes * 60 * 1000;
export const SESSION_ACTIVITY_DEBOUNCE_MS = 1000;
/** Cookie max lifetime aligned with JWT ceiling (8 hours). */
export const SESSION_COOKIE_MAX_AGE_MS = 8 * 60 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

@Injectable({
  providedIn: 'root',
})
export class SessionService implements OnDestroy {
  private idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private debounceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private isWatching = false;
  private isLoggingOut = false;
  private readonly boundResetIdle = () => this.resetIdleTimer();
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.resetIdleTimer();
    }
  };

  constructor(
    private cookieService: CookieService,
    private ngZone: NgZone,
    private httpService: HttpService,
    @Optional() @Inject(OKTA_AUTH) private oktaAuth: OktaAuth | null
  ) {}

  startIdleWatch(): void {
    if (this.isWatching || !this.cookieService.get('access_token')) {
      return;
    }

    this.isWatching = true;
    ACTIVITY_EVENTS.forEach((event) =>
      document.addEventListener(event, this.boundResetIdle, { passive: true })
    );
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.scheduleIdleLogout();
  }

  stopIdleWatch(): void {
    if (!this.isWatching) {
      return;
    }

    this.isWatching = false;
    ACTIVITY_EVENTS.forEach((event) =>
      document.removeEventListener(event, this.boundResetIdle)
    );
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.clearIdleTimeout();

    if (this.debounceTimeoutId) {
      clearTimeout(this.debounceTimeoutId);
      this.debounceTimeoutId = null;
    }
  }

  ngOnDestroy(): void {
    this.stopIdleWatch();
  }

  async logout(): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    this.stopIdleWatch();
    await this.logoutBackend();
    await this.signOutOkta();
    this.clearSession();
    window.location.href = `${environment.access}/login?logout=1`;
  }

  logoutDueToInactivity(): void {
    console.warn('SessionService: Inactivity timeout — logging out');
    this.logout();
  }

  private resetIdleTimer(): void {
    if (!this.isWatching) {
      return;
    }

    if (this.debounceTimeoutId) {
      clearTimeout(this.debounceTimeoutId);
    }

    this.debounceTimeoutId = setTimeout(() => {
      this.ngZone.run(() => this.scheduleIdleLogout());
    }, SESSION_ACTIVITY_DEBOUNCE_MS);
  }

  private scheduleIdleLogout(): void {
    this.clearIdleTimeout();
    this.idleTimeoutId = setTimeout(() => {
      this.ngZone.run(() => this.logoutDueToInactivity());
    }, SESSION_IDLE_MS);
  }

  private clearIdleTimeout(): void {
    if (this.idleTimeoutId) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
  }

  private clearSession(): void {
    this.cookieService.deleteAll(undefined, environment.domain);
    sessionStorage.removeItem('isLoggingOut');
    localStorage.removeItem('teamAssessmentSearchQuery');
  }

  private async logoutBackend(): Promise<void> {
    if (!this.cookieService.get('access_token')) {
      return;
    }

    try {
      await firstValueFrom(this.httpService.post('auth/logout', {}));
    } catch {
      // Continue logout even if the backend call fails
    }
  }

  private async signOutOkta(): Promise<void> {
    if (!this.oktaAuth) {
      return;
    }

    try {
      await this.oktaAuth.signOut({
        postLogoutRedirectUri: `${environment.access}/login?logout=1`,
      });
    } catch {
      // Continue logout even if Okta signOut fails (e.g. revocation network error)
    } finally {
      // Belt-and-suspenders: ensure Okta's persisted tokens are gone even if
      // signOut()'s revocation call failed partway through, so the login page's
      // auth check doesn't see stale credentials and bounce back in.
      try {
        await this.oktaAuth.tokenManager.clear();
      } catch {
        // no-op
      }
    }
  }
}
